import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { courseService } from '../services/courseService';
import { useAuth } from './AuthContext';

const QuizContext = createContext();

// Legacy systems stored "0" for skipped questions — filter them out as unanswered
const sanitizeAnswers = (answers) => {
    if (!answers || typeof answers !== 'object') return {};
    const clean = {};
    for (const [qId, ans] of Object.entries(answers)) {
        if (ans !== 0 && ans !== '0' && ans !== null && ans !== undefined) {
            clean[qId] = ans;
        }
    }
    return clean;
};

export const QuizProvider = ({ children }) => {
    const [quizId, setQuizId] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [courseId, setCourseId] = useState(null);
    const [courseTitle, setCourseTitle] = useState('');
    const [questions, setQuestions] = useState([]);
    const [onDemandAnswers, setOnDemandAnswers] = useState({});
    const [answers, setAnswers] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [mode, setMode] = useState('classic');
    const [isPaused, setIsPaused] = useState(false);
    const [isFinished, setIsFinished] = useState(false);
    const [timeInSeconds, setTimeInSeconds] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const timerRef = useRef(null);
    const latestValuesRef = useRef({});

    const { user } = useAuth();

    useEffect(() => {
        latestValuesRef.current = {
            quizId, courseId, answers, currentQuestionIndex, mode, timeInSeconds, isFinished, sessionId
        };
    }, [quizId, courseId, answers, currentQuestionIndex, mode, timeInSeconds, isFinished, sessionId]);

    const persistSession = useCallback(async () => {
        const current = latestValuesRef.current;
        if (!current.quizId || current.isFinished || !current.courseId) return;

        try {
            const result = await courseService.saveQuizSession(current.courseId, {
                answers: current.answers,
                currentQuestionIndex: current.currentQuestionIndex,
                mode: current.mode,
                timeInSeconds: current.timeInSeconds
            });
            if (result?.sessionId && !current.sessionId) {
                setSessionId(result.sessionId);
            }
        } catch (error) {
            console.error('Error persisting quiz session:', error);
        }
    }, []);

    // Timer
    useEffect(() => {
        if (quizId && !isPaused && !isFinished) {
            timerRef.current = setInterval(() => {
                setTimeInSeconds(prev => prev + 1);
            }, 1000);
        } else {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [quizId, isPaused, isFinished]);

    // Autosave on significant events
    useEffect(() => {
        const debouncedSave = setTimeout(persistSession, 1000);
        return () => clearTimeout(debouncedSave);
    }, [answers, currentQuestionIndex, isPaused, persistSession]);

    const checkExistingSession = async (cId) => {
        try {
            return await courseService.getQuizSession(cId);
        } catch {
            return null;
        }
    };

    const startQuiz = async (cId, initialMode = 'classic', cTitle = '') => {
        // Pre-flight: verify session is still valid (detects another device login)
        try { await api.checkSession(); } catch { return; }
        setIsLoading(true);
        const id = `quiz-${cId}-${Date.now()}`;

        try {
            // Check for existing session
            const savedSession = await courseService.getQuizSession(cId);

            // Fetch questions
            const fetchedQuestions = await courseService.getQuizQuestions(cId);

            // Shuffle options
            const processedQuestions = fetchedQuestions.map(q => ({
                ...q,
                options: q.options ? [...q.options].sort(() => Math.random() - 0.5) : []
            }));

            setQuestions(processedQuestions);
            setOnDemandAnswers({});
            setCourseId(cId);
            if (cTitle) setCourseTitle(cTitle);

            if (savedSession) {
                setQuizId(id);
                setSessionId(savedSession.sessionId);
                const cleanAnswers = sanitizeAnswers(savedSession.answers);
                setAnswers(cleanAnswers);

                let resumeIndex = savedSession.currentQuestionIndex || 0;
                const sessionMode = savedSession.mode || initialMode;
                if (sessionMode === 'flash' && cleanAnswers) {
                    const currentQuestionId = processedQuestions[resumeIndex]?.id;
                    if (currentQuestionId && cleanAnswers[currentQuestionId]) {
                        resumeIndex = Math.min(resumeIndex + 1, processedQuestions.length - 1);
                    }
                }

                setCurrentQuestionIndex(resumeIndex);
                setMode(sessionMode);
                setTimeInSeconds(savedSession.timeInSeconds || 0);
            } else {
                setQuizId(id);
                setSessionId(null);
                setAnswers({});
                setCurrentQuestionIndex(0);
                setMode(initialMode);
                setTimeInSeconds(0);
            }

            setIsPaused(false);
            setIsFinished(false);
        } catch (error) {
            console.error('Error starting quiz:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadAnswers = async () => {
        if (!courseId) return;
        try {
            const data = await courseService.getQuizAnswers(courseId);
            setOnDemandAnswers(prev => ({ ...prev, ...data }));
            return data;
        } catch (error) {
            console.error('Error loading quiz answers:', error);
        }
    };

    const pauseQuiz = () => setIsPaused(true);
    const resumeQuiz = () => setIsPaused(false);

    const stopQuiz = async () => {
        await persistSession();
        setQuizId(null);
    };

    const restartQuiz = (newMode) => {
        setAnswers({});
        setOnDemandAnswers({});
        setCurrentQuestionIndex(0);
        setTimeInSeconds(0);
        setIsFinished(false);
        setMode(newMode);
        persistSession();
    };

    const submitAnswer = (questionId, answerId) => {
        if (isFinished) return;
        setAnswers(prev => ({ ...prev, [questionId]: answerId }));
    };

    const finishQuiz = async () => {
        const allAnswers = await loadAnswers();
        setIsFinished(true);

        let correctCount = 0;
        questions.forEach((q) => {
            const secret = allAnswers?.[q.id] || onDemandAnswers[q.id];
            if (answers[q.id] !== undefined && secret && String(answers[q.id]) === String(secret.correctReponseId)) {
                correctCount++;
            }
        });

        const score = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;

        await courseService.saveQuizHistory(sessionId, {
            courseId,
            score,
            timeInSeconds,
            answers,
            mode
        });

        return score;
    };

    const value = {
        quizId,
        courseId,
        courseTitle,
        questions,
        onDemandAnswers,
        answers,
        currentQuestionIndex,
        setCurrentQuestionIndex,
        mode,
        setMode,
        isPaused,
        setIsPaused,
        isFinished,
        timeInSeconds,
        isLoading,
        checkExistingSession,
        startQuiz,
        loadAnswers,
        pauseQuiz,
        resumeQuiz,
        stopQuiz,
        restartQuiz,
        submitAnswer,
        finishQuiz
    };

    return <QuizContext.Provider value={value}>{children}</QuizContext.Provider>;
};

export const useQuiz = () => {
    const context = useContext(QuizContext);
    if (!context) throw new Error('useQuiz must be used within a QuizProvider');
    return context;
};
